import { SWR_KEYS } from '@bulknutrients/bulk-shared-lib/constants';
import type { BulkAPIErrorResponse } from '@bulknutrients/bulk-shared-lib/interfaces/BulkAPI';
import type {
  CustomCart,
  CustomCartItem,
  DigitalItem,
  PhysicalItem,
} from '@bulknutrients/bulk-shared-lib/interfaces/ICart';
import { cartValueWithoutGiftCertificates } from '@bulknutrients/bulk-shared-lib/lib/cart';
import { mergeStyles } from '@bulknutrients/bulk-shared-lib/utils';
import { ErrorAlert } from '@components/Alerts/ErrorAlert';
import { Button, ButtonType } from '@components/Button';
import { BigCommerceCheckoutButton } from '@components/Button/BigCommerceCheckoutButton';
import { Input } from '@components/FormComponents';
import { HorizontalDivider } from '@components/HorizontalDivider/HorizontalDivider';
import { LoginForm } from '@components/LoginForm';
import { SkeletonText } from '@components/Placeholders/Skeleton';
import { Paragraph } from '@components/Typography/Paragraph/Paragraph';
import { TextLink } from '@components/Typography/TextLink/TextLink';
import { useCustomer } from '@hooks/customer/useCustomer';
import { log } from '@lib/logs';
import type React from 'react';
import {
  type ReactElement,
  type ReactNode,
  useEffect,
  useRef,
  useState,
} from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useSWRConfig } from 'swr';
import { CreateAccountLoyaltyPrompt } from './LoyaltyPrompt';

const rowStyle =
  'py-2 px-0 mb-1 border-solid border-0 border-b-grey-light border-b-2 grid grid-cols-[auto_140px] sm:grid-cols-[repeat(2,200px)]';

function ClickableText({
  children,
  className,
  onClick,
}: {
  children: ReactNode;
  className?: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={mergeStyles(
        className ?? '',
        'link w-fit-content cursor-pointer justify-self-end border-none bg-transparent p-0 font-semibold text-inherit underline decoration-grey-mid decoration-1 underline-offset-[0.1em] duration-500 hover:text-black hover:decoration-orange hover:decoration-3 hover:duration-200 dark:hover:text-white'
      )}
    >
      {children}
    </button>
  );
}

function CartSummaryLoginForm() {
  return (
    <div className="card col-span-ful mx-0l my-2.5 p-4 rounded-2xl overflow-hidden">
      <Paragraph>
        Please log in or{' '}
        <TextLink href="/create-account">create an account</TextLink> to enter a
        coupon code.
      </Paragraph>
      <LoginForm />
    </div>
  );
}

function CouponForm({ cart }: { cart: CustomCart }) {
  const [error, setError] = useState(false);
  const [errorMessage, setErrorMessage] = useState<ReactNode>();
  const [disabled, setDisabled] = useState(false);
  const [loading, setLoading] = useState(false);

  const { mutate } = useSWRConfig();
  const { customer } = useCustomer();

  const rhfMethods = useForm<any>({
    mode: 'onTouched',
    defaultValues: {
      coupon: '',
    },
  });

  const handleSubmit = async (e: { coupon: string }) => {
    // Ensure have entered a code
    if (!e.coupon || e.coupon === '') {
      setError(true);
      setErrorMessage(<Paragraph>Please enter your coupon code</Paragraph>);
      return;
    }
    setLoading(true);

    try {
      setDisabled(true);
      const couponAdd = await fetch('/api/cart/add-coupon', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          checkoutId: cart.id,
          coupon: e.coupon.trim(),
        }),
      });

      // Error on server, show the problem
      if (couponAdd.status !== 201) {
        couponAdd.json().then((err: BulkAPIErrorResponse) => {
          setError(true);
          setErrorMessage(
            <Paragraph>
              {err ? err.error.message[0] : 'Unexpected error'}
            </Paragraph>
          );
          setLoading(false);
        });
      }
    } catch (err) {
      setError(true);
      setLoading(false);
      setErrorMessage(<Paragraph>Unexpected error</Paragraph>);
      log({ error: err, location: 'CouponForm handleSubmit' });
    } finally {
      setLoading(false);
      setDisabled(false);
      mutate(SWR_KEYS.cart);
    }
  };

  if (loading)
    return (
      // prevent layout shift
      <div className="min-w-[200px]">
        <SkeletonText heightPixels={330} />
      </div>
    );

  if (error)
    return (
      <ErrorAlert show={error} setShow={setError}>
        {errorMessage}
      </ErrorAlert>
    );

  return (
    <div>
      {customer ? (
        <FormProvider {...rhfMethods}>
          <div className="col-span-full mx-0 my-1">
            <form
              className="flex items-stretch"
              onSubmit={rhfMethods.handleSubmit(handleSubmit)}
            >
              <Input
                label=""
                type="text"
                name="coupon"
                id="coupon"
                className="rounded-r-none"
                placeholder="Enter your coupon code"
              />

              <div className="w-full gap-0 pt-1">
                <Button
                  type={ButtonType.submit}
                  disabled={disabled}
                  className="rounded-l-none"
                  buttonStyle="secondary"
                >
                  Apply
                </Button>
              </div>
            </form>
          </div>
        </FormProvider>
      ) : (
        <CartSummaryLoginForm />
      )}
    </div>
  );
}

function CouponApplied({ cart }: { cart: CustomCart }) {
  const [error, setError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('Unknown error occurred');
  const [loading, setLoading] = useState(false);
  const { mutate } = useSWRConfig();

  const removeCoupon = async () => {
    const body = {
      checkoutId: cart.id,
      coupon: cart.coupons?.[0] ? cart.coupons[0].code.trim() : '',
    };

    setLoading(true);

    await fetch('/api/cart/remove-coupon', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
    })
      .then((res) => {
        if (!res.ok) {
          res.json().then((err: BulkAPIErrorResponse) => {
            setError(true);
            setErrorMessage(err ? err.error.message[0] : 'Unexpected error');
          });
        }
      })
      .catch((err) => {
        log({ error: err, location: 'CouponApplied removeCoupon' });
      })
      .finally(() => {
        setLoading(false);
        mutate(SWR_KEYS.cart);
      });
  };

  if (loading)
    return (
      <div className={rowStyle}>
        <SkeletonText heightPixels={30} />
      </div>
    );

  if (error)
    return (
      <ErrorAlert show={error} setShow={setError}>
        {errorMessage}
      </ErrorAlert>
    );

  return (
    <div className={rowStyle}>
      <span>Coupon ({cart.coupons?.[0] ? cart.coupons[0].code : ''})</span>
      <span className="justify-self-end">
        -$
        {cart?.coupons?.[0]
          ? cart?.coupons[0].discounted_amount.toFixed(2)
          : ''}
      </span>
      <ClickableText onClick={removeCoupon} className="col-span-full">
        Remove
      </ClickableText>
    </div>
  );
}

// Controls whether to display or hide coupon form
function CouponController({
  showCouponInput,
  setShowCouponInput,
}: {
  showCouponInput: boolean;
  setShowCouponInput: React.Dispatch<React.SetStateAction<boolean>>;
}) {
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return function cleanup() {
      mounted.current = false;
    };
  });

  const revealCoupon = () => {
    if (mounted.current) setShowCouponInput(!showCouponInput);
  };

  return (
    <div className={rowStyle}>
      <span>Coupon Code:</span>
      <ClickableText onClick={revealCoupon}>
        {showCouponInput ? 'Cancel' : 'Add Coupon'}
      </ClickableText>
    </div>
  );
}

export function CartSummary({
  cart,
  items,
}: {
  cart?: CustomCart | null;
  items: CustomCartItem[];
}): ReactElement {
  const [subtotal, setSubtotal] = useState(0);
  const [showCouponInput, setShowCouponInput] = useState(false);

  // Set subtotal
  useEffect(() => {
    let mounted = true;
    if (cart && mounted) {
      const sub = [
        ...cart.line_items.digital_items,
        ...cart.line_items.physical_items,
      ].reduce((acc: number, item: PhysicalItem | DigitalItem) => {
        acc +=
          item.quantity *
          items.filter(
            (cur: CustomCartItem) => cur.variant?.variantId === item.variant_id
          )[0]?.original_price;
        return acc;
      }, 0);

      const withGiftCerts =
        sub +
        cart.line_items.gift_certificates.reduce(
          (acc, cur) => acc + cur.amount,
          0
        );

      setSubtotal(withGiftCerts || cart.cart_amount);
    }
    return function cleanup() {
      mounted = false;
    };
  }, [cart, items]);

  return (
    <div className="my-2.5 flex flex-col items-stretch sm:items-center">
      <div className={rowStyle}>
        <span className="text-left">Subtotal:</span>
        <span className="text-right">${subtotal.toFixed(2)}</span>
      </div>

      {cart && subtotal > cart.base_amount && (
        <div className={rowStyle}>
          <span className="text-left">Discounts:</span>
          <span className="text-right">
            -${(subtotal - cart?.base_amount).toFixed(2)}
          </span>
        </div>
      )}

      {cart && (!cart.coupons || cart.coupons.length === 0) && (
        <CouponController
          showCouponInput={showCouponInput}
          setShowCouponInput={setShowCouponInput}
        />
      )}

      {showCouponInput &&
        cart &&
        (!cart.coupons || cart.coupons.length === 0) &&
        (cart.line_items.physical_items.length > 0 ? (
          <CouponForm cart={cart} />
        ) : (
          <div>Please add a product to your cart to enter a coupon code</div>
        ))}

      {cart?.coupons && cart.coupons.length > 0 && (
        <CouponApplied cart={cart} />
      )}

      <div className={rowStyle}>
        <span className="text-left">Total before shipping:</span>
        <span className="text-right">${cart?.cart_amount.toFixed(2)}</span>
      </div>
      <div className={rowStyle}>
        <span className="text-left">
          Proceed to checkout for shipping options
        </span>
      </div>
      <BigCommerceCheckoutButton
        styledButton={true}
        location="Cart summary"
        className="my-2"
      />
      <HorizontalDivider className="my-2.5 border-none via-grey-mid p-0" />
      {cart ? (
        <CreateAccountLoyaltyPrompt
          cartValue={cartValueWithoutGiftCertificates(cart)}
        />
      ) : null}
    </div>
  );
}
